import React, { useCallback } from 'react';
import { Box, Flex, Icon, NakedButton } from '@qga/roo-ui/components';
import { OfferWrapper } from '../primitives';
import { useSelector } from 'react-redux';
import ExpandedClickableArea from 'components/ExpandedClickableArea';
import PromotionalSashNew from 'components/PromotionalSashNew';
import { useDataLayer } from 'hooks/useDataLayer';
import type { Offer } from 'types/property';
import OfferDescription from './components/OfferDescription';
import OfferPricing from './components/OfferPricing';
import { getProperty } from 'store/property/propertySelectors';
import useStandardCampaign from 'hooks/optimizely/useStandardCampaign';
import { getCampaignDisclaimerAndPromotionMessage, getPromotionalSashMessage } from 'lib/campaigns/helpers';
import { getCampaignDefaultSash } from 'store/campaign/campaignSelectors';
interface OfferSummaryProps {
  offer: Offer;
  toggleOfferExpanded: (value: boolean) => void;
}

const CollapsedOffer = ({ offer, toggleOfferExpanded }: OfferSummaryProps) => {
  const { allocationsAvailable, name: offerName, charges, inclusions, cancellationPolicy, depositPay, luxOffer, type } = offer;

  const globalCampaignDefaultSash = useSelector(getCampaignDefaultSash);
  const property = useSelector(getProperty);

  const { isStandardCampaignEnabled, isGeoTargeted, campaignGeoConfig } = useStandardCampaign();

  const { campaignSpecificPromotionMessage } = getCampaignDisclaimerAndPromotionMessage(
    isStandardCampaignEnabled,
    property.address,
    isGeoTargeted,
    campaignGeoConfig,
  );

  const promotionSash = getPromotionalSashMessage(offer, globalCampaignDefaultSash, campaignSpecificPromotionMessage);

  const { emitInteractionEvent } = useDataLayer();
  const isClassic = offer.type === 'classic';

  const onExpandOffer = useCallback(() => {
    toggleOfferExpanded(true);
    emitInteractionEvent({ type: 'Room Offer Details', value: 'Offer Expanded' });
  }, [toggleOfferExpanded, emitInteractionEvent]);

  return (
    <ExpandedClickableArea>
      <OfferWrapper flexDirection="column" data-testid="offer-card-new">
        {promotionSash && (
          <Box position="absolute" left={0} top={0}>
            <PromotionalSashNew promotionName={promotionSash} type="corner" />
          </Box>
        )}
        <Flex data-testid="expand-icon" flexDirection={'row'} justifyContent="flex-end">
          <NakedButton
            onClick={onExpandOffer}
            aria-label="Expand offer details"
            data-expanded-clickable-area-target
            p={0}
            data-testid="expand-offer-summary"
          >
            <Icon name="expandMore" size={24} />
          </NakedButton>
        </Flex>
        <Flex data-testid="offer-card-body" flexDirection={['column', 'row', 'row']} width="100%" flexWrap="wrap">
          <OfferDescription offerName={offerName} inclusions={inclusions} onClick={onExpandOffer} />
          <OfferPricing
            isClassic={isClassic}
            charges={charges}
            allocationsAvailable={allocationsAvailable}
            cancellationPolicy={cancellationPolicy}
            depositPay={depositPay}
            offerType={type}
            isLuxuryOffer={!!luxOffer}
          />
        </Flex>
      </OfferWrapper>
    </ExpandedClickableArea>
  );
};

export default CollapsedOffer;
